import React, { useEffect, useMemo, useState } from 'react';

// Configuration API - Utiliser la variable d'environnement VITE_API_URL avec fallback pour localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api';
// console.log('[TemplatePreview] API_BASE_URL utilisé:', API_BASE_URL); // Commenté pour production
// console.log('[TemplatePreview] import.meta.env.VITE_API_URL:', import.meta.env.VITE_API_URL); // Commenté pour production

interface TemplatePreviewProps {
  /** Nom lisible du template (ex: ModernBusiness001) */
  templateName: string;
  /** ID exact du template pour l'API (ex: modern_modernbusiness001) */
  templateApiId?: string;
  /** Données à envoyer à l'API de preview (si useLivePreview) */
  userData?: any;
  /** Active le rendu réel via /api/cv/preview/ (par défaut true) */
  useLivePreview?: boolean;
  scale?: number;
  className?: string;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ 
  templateName, 
  templateApiId,
  userData,
  useLivePreview = true,
  scale = 0.3,
  className = ""
}) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Préparer la charge utile pour l'API uniquement quand les inputs changent
  const previewPayload = useMemo(() => {
    if (!useLivePreview || !templateApiId || !userData) {
      // console.log('[TemplatePreview] Preview payload non créé:', { useLivePreview, templateApiId, userData: !!userData }); // Commenté pour production
      return null;
    }
    const payload = {
      template_id: templateApiId,
      user_data: userData
    };
    // console.log('[TemplatePreview] Preview payload créé:', { template_id: payload.template_id, user_data_keys: Object.keys(payload.user_data || {}) }); // Commenté pour production
    return payload;
  }, [useLivePreview, templateApiId, userData]);

  // Charger le HTML réel si demandé
  useEffect(() => {
    let canceled = false;
    const fetchPreview = async () => {
      if (!previewPayload) {
        setHtmlContent(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const url = `${API_BASE_URL}/cv/preview/`;
        // console.log('[TemplatePreview] Envoi requête preview:', { url, template_id: previewPayload.template_id }); // Commenté pour production
        
        // Préparer les headers - ne pas envoyer Idempotency-Key en local (localhost)
        const headers: HeadersInit = { 
          'Content-Type': 'application/json'
        };
        
        // Ajouter Idempotency-Key uniquement si on n'est pas en localhost
        if (!API_BASE_URL.includes('localhost') && !API_BASE_URL.includes('127.0.0.1')) {
          const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
          headers['Idempotency-Key'] = idempotencyKey;
        }
        
        const resp = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(previewPayload)
        });
        
        // Lire la réponse même en cas d'erreur pour obtenir le message d'erreur
        const data = await resp.json().catch(() => ({ error: 'Impossible de parser la réponse' }));
        
        if (!resp.ok) {
          // Afficher le message d'erreur du backend si disponible
          const errorMessage = data.error || data.message || `HTTP ${resp.status}`;
          const errorCode = data.error_code || '';
          // console.error('[TemplatePreview] Erreur preview:', { status: resp.status, error: errorMessage, errorCode, data }); // Commenté pour production
          throw new Error(errorCode ? `${errorCode}: ${errorMessage}` : errorMessage);
        }
        
        if (!data?.html_content) {
          throw new Error('Réponse sans html_content');
        }
        if (!canceled) {
          setHtmlContent(data.html_content);
        }
      } catch (e: any) {
        if (!canceled) {
          // console.error('[TemplatePreview] Erreur fetchPreview:', e); // Commenté pour production
          setError(e.message || 'Erreur de preview');
          setHtmlContent(null);
        }
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    };
    fetchPreview();
    return () => { canceled = true; };
  }, [previewPayload]);

  // Calculer l'échelle optimale pour remplir au maximum le cadre disponible
  // Format A4: 210mm x 297mm (ratio ~0.707)
  // Cadres: aspect 3:4 (ratio 0.75) - proche du format A4
  // On calcule l'échelle pour que le template remplisse le cadre en gardant son ratio
  const getOptimalScale = () => {
    // Pour la modal (échelle spécifique)
    if (scale === 1.5) {
      return 0.7; // Échelle pour la modal - plus grand pour utiliser l'espace
    }
    if (scale === 0.4) {
      return 0.4; // Échelle pour la modal
    }
    // Pour les cartes en mode grille (aspect 3:4)
    if (className.includes('aspect-[3/4]') || scale >= 0.5) {
      // Le cadre a un ratio 3:4 (0.75), le template A4 a un ratio ~0.707
      // Le template est plus haut que large, donc la hauteur sera le facteur limitant
      // On veut que le template remplisse ~90% de la hauteur du cadre pour laisser un peu de marge
      // En supposant un cadre de ~400-500px de hauteur: (450 * 0.9) / 1123 ≈ 0.36
      // Mais pour vraiment remplir l'espace, on augmente encore
      return 0.45; // Échelle optimisée pour mieux remplir le cadre
    }
    // Pour les cartes en mode liste (petites)
    if (scale === 0.3) {
      return 0.25; // Échelle pour les cartes liste
    }
    return 0.3; // Échelle par défaut
  };

  const optimalScale = getOptimalScale();

  const renderLivePreview = () => {
    if (!templateApiId || !userData) {
      return (
        <div className="w-full h-full flex items-center justify-center text-gray-500 text-center px-4">
          Fournis templateId et userData pour activer la prévisualisation.
        </div>
      );
    }
    if (loading) {
      return (
        <div className="w-full h-full flex items-center justify-center text-gray-500">
          Prévisualisation en cours...
        </div>
      );
    }
    if (error) {
      return (
        <div className="w-full h-full flex items-center justify-center text-red-600 text-sm text-center px-4">
          Erreur de prévisualisation<br />{error}
        </div>
      );
    }
    if (htmlContent) {
      // Format A4 en pixels: 210mm = 794px, 297mm = 1123px (à 96 DPI)
      const a4WidthPx = 794;
      const a4HeightPx = 1123;
      
      return (
        <div 
          className="w-full h-full"
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
        <iframe
          title="CV preview"
            style={{ 
              width: `${a4WidthPx}px`,
              height: `${a4HeightPx}px`,
              transform: `scale(${optimalScale})`,
              transformOrigin: 'center center',
              border: 'none',
              display: 'block',
              flexShrink: 0
            }}
          srcDoc={htmlContent}
            sandbox="allow-same-origin allow-scripts"
        />
        </div>
      );
    }
    // Pas encore de HTML : fallback visuel léger
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-400">
        Prévisualisation en attente
      </div>
    );
  };

  return (
    <div 
      className={`template-preview w-full h-full ${className}`} 
      style={{ 
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {useLivePreview
        ? renderLivePreview()
        : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            Mode statique désactivé. Active useLivePreview pour afficher le rendu réel.
          </div>
        )
      }
    </div>
  );
};